import { useCallback, useLayoutEffect, useState } from 'react';
import type { MediaDocType } from '@ast-data/shared';

/**
 * The measuring half of drawing a plate on screen: how wide it is shown, and
 * what shape each picture has. The drawing itself is in plateView.
 */

/** The width of an element, kept current as it resizes. */
export function useElementWidth<Element extends HTMLElement>() {
    const [node, setNode] = useState<Element | null>(null);
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        if (!node) {
            return;
        }
        // Reports the current size once on observe, then on every change.
        const observer = new ResizeObserver(([entry]) =>
            setWidth(entry.contentRect.width)
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [node]);
    return { ref: setNode, width };
}

/** A sketch's shape, from its scene; null for a photo or an old scene. */
function sceneAspect(media: MediaDocType): number | null {
    if (media.kind !== 'sketch' || !media.scene) {
        return null;
    }
    try {
        const aspect = (JSON.parse(media.scene) as { aspect?: unknown }).aspect;
        return typeof aspect === 'number' && aspect > 0 ? aspect : null;
    } catch {
        return null;
    }
}

/**
 * Width ÷ height of each picture. A sketch knows its own; a photo's is read off
 * its preview once that has loaded (the server keeps the shape when it scales
 * one down), and until then the geometry takes it for 4:3.
 */
export function useMediaAspects() {
    const [measured, setMeasured] = useState<ReadonlyMap<string, number>>(
        () => new Map()
    );
    const report = useCallback((id: string, aspect: number) => {
        setMeasured((current) =>
            current.get(id) === aspect
                ? current
                : new Map(current).set(id, aspect)
        );
    }, []);
    const aspectOf = useCallback(
        (media: MediaDocType) => measured.get(media.id) ?? sceneAspect(media),
        [measured]
    );
    return { aspectOf, report };
}
